import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../ui';
import SearchableSelect from './SearchableSelect';
import HelpTip from './HelpTip';
import { api } from '../api';

/**
 * StockAlertForm
 *
 * Form de alta/edición de un umbral de stock mínimo (StockAlert).
 * Se renderiza dentro del Dialog del DS desde la pantalla Ajustes.
 *
 * Campos:
 *   - tipo (asset_type_id)    obligatorio · SearchableSelect de /tipos (id entero, único por (scope,slug))
 *   - empresa (empresa_id)    opcional   · null = "todas las empresas".
 *                              Las opciones se cargan según el scope del
 *                              tipo elegido (interno/colab → /options
 *                              ?link=company&scope=…). Si aún no hay
 *                              tipo, el selector queda deshabilitado.
 *   - stock_minimo            entero >= 0 (default 1)
 *   - is_active               toggle (default true)
 *
 * Props:
 *   initial:   row existente (modo editar) o null (alta)
 *   onSubmit:  (payload) => Promise  — devuelve cuando se haya creado/editado
 *   onCancel:  () => void
 */

export default function StockAlertForm({ initial, onSubmit, onCancel }) {
  const isEdit = !!initial;

  const [tipos, setTipos] = useState([]);
  const [tiposLoading, setTiposLoading] = useState(true);

  // El tipo se referencia por su id (entero, único por (scope, slug)). El
  // SearchableSelect trabaja con value string; se convierte a Number en submit.
  const [assetTypeId, setAssetTypeId] = useState(
    initial?.asset_type_id != null ? String(initial.asset_type_id) : ''
  );
  const [empresaId, setEmpresaId] = useState(
    initial?.empresa_id == null ? '' : String(initial.empresa_id)
  );
  const [stockMin, setStockMin] = useState(
    initial?.stock_minimo != null ? String(initial.stock_minimo) : '1'
  );
  const [severidad, setSeveridad] = useState(initial?.severidad || 'warning');
  const [isActive, setIsActive] = useState(
    initial?.is_active !== undefined ? !!initial.is_active : true
  );

  const [empresas, setEmpresas] = useState([]);
  const [empresasLoading, setEmpresasLoading] = useState(false);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Carga inicial de tipos (interno + colab) para el selector
  useEffect(() => {
    let alive = true;
    (async () => {
      setTiposLoading(true);
      try {
        const res = await api.get('/tipos');
        if (!alive) return;
        setTipos(res?.data || []);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.error || e.message);
      } finally {
        if (alive) setTiposLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const tipoSeleccionado = useMemo(
    () => tipos.find((t) => String(t.id) === String(assetTypeId)) || null,
    [tipos, assetTypeId]
  );

  // Si el ÁMBITO del tipo cambia, la empresa elegida (de la lista del ámbito
  // anterior) ya no aplica → resetear empresa_id. Un id de negocios no vale
  // como empresa colaboradora y viceversa (empresa_id es polimórfico). El ref
  // se inicializa al scope actual para NO resetear en el montaje (modo editar).
  const prevScopeRef = useRef(tipoSeleccionado?.scope ?? null);
  useEffect(() => {
    const sc = tipoSeleccionado?.scope ?? null;
    if (prevScopeRef.current !== null && sc !== null && sc !== prevScopeRef.current) {
      setEmpresaId('');
    }
    prevScopeRef.current = sc;
  }, [tipoSeleccionado]);

  // Al cambiar el tipo, cargar las empresas de su scope
  useEffect(() => {
    let alive = true;
    if (!tipoSeleccionado) {
      setEmpresas([]);
      return undefined;
    }
    setEmpresasLoading(true);
    (async () => {
      try {
        const res = await api.get('/options', {
          link: 'company',
          scope: tipoSeleccionado.scope || 'interno',
        });
        if (alive) setEmpresas(res?.data || []);
      } catch (e) {
        if (alive) setEmpresas([]);
      } finally {
        if (alive) setEmpresasLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [tipoSeleccionado]);

  const tipoOptions = useMemo(
    () =>
      tipos
        // Un umbral de stock solo tiene sentido en tipos con gestión de stock.
        .filter((t) => t.has_stock === true || t.has_stock === 1)
        .map((t) => ({
          id: t.id, // entero, único por (scope, slug) → sin colisión entre ámbitos
          nombre: `${t.nombre} · ${t.scope === 'colab' ? 'colab' : 'GRUPOLAE'}`,
        })),
    [tipos]
  );

  const empresaOptions = useMemo(
    () => [
      { id: '', nombre: 'Todas las empresas (regla global)' },
      ...empresas,
    ],
    [empresas]
  );

  async function handleSubmit() {
    setErr(null);
    if (!assetTypeId) {
      setErr('Selecciona un tipo de artículo.');
      return;
    }
    const n = Number(stockMin);
    if (!Number.isInteger(n) || n < 0) {
      setErr('Stock mínimo debe ser un entero >= 0.');
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        asset_type_id: Number(assetTypeId),
        empresa_id: empresaId === '' ? null : Number(empresaId),
        stock_minimo: n,
        severidad,
        is_active: isActive,
      });
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Error al guardar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 text-sm">
      <div>
        <label className="text-xs text-slate-500 mb-1 block">
          Tipo de artículo
        </label>
        <SearchableSelect
          value={assetTypeId}
          onChange={setAssetTypeId}
          options={tipoOptions}
          disabled={isEdit || tiposLoading}
          placeholder={tiposLoading ? 'Cargando tipos…' : 'Selecciona un tipo'}
        />
        {isEdit && (
          <p className="text-[11px] text-slate-400 mt-1">
            El tipo no se puede cambiar al editar un umbral existente.
          </p>
        )}
      </div>

      <div>
        <label className="text-xs text-slate-500 mb-1 block">Empresa</label>
        <SearchableSelect
          value={empresaId}
          onChange={setEmpresaId}
          options={empresaOptions}
          disabled={!tipoSeleccionado || empresasLoading}
          placeholder={
            !tipoSeleccionado
              ? 'Primero elige un tipo'
              : empresasLoading
              ? 'Cargando empresas…'
              : 'Todas las empresas'
          }
        />
        <p className="text-[11px] text-slate-400 mt-1">
          Vacío = la regla aplica a todas las empresas del tipo.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
            Stock mínimo
            <HelpTip>Por debajo de esta cantidad se dispara el aviso de stock bajo.</HelpTip>
          </label>
          <input
            type="number"
            min="0"
            value={stockMin}
            onChange={(e) => setStockMin(e.target.value)}
            className="w-full border border-slate-200 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">Estado</label>
          <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="text-sm text-slate-700">Activo</span>
          </label>
        </div>
      </div>

      <div>
        <label className="text-xs text-slate-500 mb-1 block">Severidad</label>
        <div className="flex gap-2">
          {[
            { value: 'info', label: 'Info', activeCls: 'bg-blue-100 text-blue-800 border-blue-300' },
            { value: 'warning', label: 'Warning', activeCls: 'bg-amber-100 text-amber-800 border-amber-300' },
            { value: 'danger', label: 'Danger', activeCls: 'bg-rose-100 text-rose-800 border-rose-300' },
          ].map((opt) => {
            const active = severidad === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSeveridad(opt.value)}
                className={
                  'px-3 py-1.5 rounded-full border text-xs transition-colors ' +
                  (active
                    ? opt.activeCls + ' font-medium'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300')
                }
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-400 mt-1">
          Cómo se mostrará la alerta en el Dashboard cuando el stock
          caiga al umbral.
        </p>
      </div>

      {err && (
        <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2">
          {err}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 pt-3 mt-1 border-t border-slate-100">
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={busy}>
          {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
          {isEdit ? 'Guardar cambios' : 'Crear umbral'}
        </Button>
      </div>
    </div>
  );
}
